import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { AbstractControl, FormControl } from "@angular/forms";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import { ReplaySubject, Subject, take, takeUntil } from "rxjs";
import { SearchOption } from "../../pipes/search-pipe/search.model";
import { SearchPipe } from "../../pipes/search-pipe/search.pipe";

@Component({
  selector: "up-select-search",
  templateUrl: "./select-search.component.html",
  styleUrls: ["./select-search.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectSearchComponent implements OnInit, OnChanges, OnDestroy {
  // TODO Melhorar implementação seguindo: https://www.npmjs.com/package/ngx-mat-select-search

  selectControl: FormControl = new FormControl();

  filterControl: FormControl = new FormControl();

  /** list of options filtered by search keyword */
  filteredOptions: ReplaySubject<SearchOption[]> = new ReplaySubject<
    SearchOption[]
  >(1);

  /** flags to set the toggle all checkbox state */
  isIndeterminate = false;
  isChecked = false;

  subscription!: any;

  justOpened: boolean = true;

  oldValue: any;

  @Input() label!: string;
  @Input() required: boolean = false;
  @Input() showDefaultOption: boolean = true;
  @Input() showNone: boolean = false;
  @Input() disableDefaultOption: boolean = true;
  @Input() defaultOptionText: string = "Selecione";
  @Input() list: SearchOption[] = [];
  @Input() searchText!: string;
  @Input() control!: AbstractControl | null;
  @Input() multiple: boolean = false;
  @Input() showOkButton: boolean = true;
  @Input() okButtonText: string = "Ok";
  @Input() flat: boolean = false;

  @Output() selectChange: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("select", { static: false }) select!: MatSelect;
  @ViewChild("searchInput", { static: false }) searchInput!: ElementRef;

  @ContentChild("optionTemplate") optionTemplate?: TemplateRef<any>;

  pipe!: SearchPipe;

  /** Subject that emits when the component has been destroyed. */
  private _onDestroy = new Subject<void>();

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes["list"]) {
      this.list = changes["list"].currentValue;

      if (!changes["list"].firstChange) {
        this.setValue();
      }
    }

    if (changes && changes["control"]) {
      if (!changes["control"].firstChange) {
        this.toggleDisabled();

        this.selectControl.setValue(this.control?.value);
      }
    }
  }

  ngOnInit(): void {
    this.toggleDisabled();

    this.pipe = new SearchPipe();

    this.setValue();

    if (!this.control) throw new Error("Parameter Control is required");

    this.subscription = this.control.valueChanges.subscribe((value: any) => {
      this.selectControl.setValue(value);

      if (!this.oldValue) this.oldValue = value;
    });

    this.oldValue = this.control.value;

    // Se inscrever nas alterações do campo de filtro para filtrar conforme o usuário digita
    this.filterControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this._filter();
      });
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();

    this._onDestroy.next();
    this._onDestroy.complete();
  }

  /**
   * Filtrar as opções conforme o usuário digita
   */
  private _filter() {
    if (!this.list) return;

    let search = this.filterControl.value;

    if (!search) {
      this.filteredOptions.next(this.list?.slice());
      return;
    } else search = search.toLowerCase();

    this.filteredOptions.next(
      this.pipe.transform(
        this.list,
        search,
        this.multiple,
        this.control?.value,
      ),
    );
  }

  toggleDisabled() {
    if (this.control && this.control.disabled) this.selectControl.disable();
    else this.selectControl.enable();
  }

  onSearchTextChange(value: any): void {
    if (value && !this.multiple) {
      setTimeout(() => {
        let option: MatOption | undefined = this.select.options.get(
          !this.showDefaultOption && this.disableDefaultOption ? 1 : 2,
        );

        if (option) {
          option.select();
        }
      });
    }
  }

  onSelectionChange(value: any) {
    if (
      this.multiple &&
      !value[0] &&
      value.length &&
      !this.disableDefaultOption
    ) {
      (<any[]>value).splice(0, 1);

      value = this.list.map((x) => x.value);
    }

    if (this.control) this.control.setValue(value);

    this.selectChange.emit(value);

    this.justOpened = false;

    if (this.multiple && !value[0] && value.length) this.select.close();
  }

  keyPressed(event: KeyboardEvent): void {
    if (event.key == "Escape" && this.oldValue) {
      this.selectControl.setValue(this.oldValue);

      this.onSelectionChange(this.oldValue);
    }

    if (
      event.key != "Escape" &&
      event.key != "Enter" &&
      event.key != "NumpadEnter" &&
      event.key != "ArrowDown" &&
      event.key != "ArrowUp"
    ) {
      event.stopPropagation();
    }

    if (
      !this.justOpened &&
      (event.key == "Enter" || event.key == "NumpadEnter")
    ) {
      setTimeout(() => {
        this.onSelectionChange((<MatOption>this.select.selected)?.value);

        this.select.close();
      });
    }
  }

  selectTriggered(opened: boolean): void {
    if (opened) {
      this.justOpened = true;

      if (this.searchInput) {
        setTimeout(() => {
          this.searchInput.nativeElement.focus();
        });
      }
    } else {
      this.searchText = "";
      this.select.focus();
    }
  }

  okButtonClicked(): void {
    this.select.close();
  }

  private setValue(): void {
    // Adicionar todas as opções disponíveis ao select
    if (this.list?.length > 0) this.filteredOptions.next(this.list.slice());

    if (this.control)
      if (this.selectControl.value != this.control.value) {
        this.selectControl.setValue(this.control.value ?? "");
      }
  }

  getTitle() {
    if (this.list)
      return this.list.find((x) => x.value == this.control?.value)?.text ?? "";

    return "";
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.filteredOptions
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe((val) => {
        const value = selectAllValue ? val.map((x) => x.value) : [];

        this.control?.patchValue(value);

        this.selectChange.emit(value);
      });
  }
}
